import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

// ================================
// TYPES
// ================================

export type DatePreset = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

export interface DateRangeParams {
  preset?: DatePreset;
  startDate?: string;
  endDate?: string;
}

function buildDateParams(dateParams?: DateRangeParams): string {
  if (!dateParams) return '';
  const params = new URLSearchParams();
  if (dateParams.preset) params.append('preset', dateParams.preset);
  if (dateParams.startDate) params.append('startDate', dateParams.startDate);
  if (dateParams.endDate) params.append('endDate', dateParams.endDate);
  return params.toString();
}

export interface ExecutiveSummary {
  today: {
    revenue: number;
    orders: number;
    averageOrderValue: number;
    activeOrders: number;
  };
  yesterday: {
    revenue: number;
    orders: number;
    averageOrderValue: number;
  };
  lastWeekSameDay: {
    revenue: number;
    orders: number;
  };
}

export interface HourlySales {
  data: Array<{
    hour: number;
    revenue: number;
    orders: number;
  }>;
}

export interface TopSellingItem {
  id: string;
  name: { en: string; th: string } | string;
  quantity: number;
  revenue: number;
}

export interface BranchPerformance {
  id: string;
  name: string;
  code: string;
  revenue: number;
  orders: number;
}

export interface ExecutiveAlerts {
  lowStock: number;
  pendingPRs: number;
  pendingTransfers: number;
  expiringItems: number;
  total: number;
}

export interface RecentOrder {
  id: string;
  orderNumber: number;
  branchName: string;
  branchCode: string;
  total: number;
  itemCount: number;
  status: string;
  createdAt: string | null;
}

export interface PaymentBreakdown {
  breakdown: Array<{
    method: string;
    amount: number;
    count: number;
    percentage: number;
  }>;
  total: {
    amount: number;
    count: number;
  };
}

export interface CategorySales {
  categories: Array<{
    id: string;
    name: string;
    quantity: number;
    revenue: number;
  }>;
}

export interface SalesByDay {
  data: Array<{
    day: string;
    dayIndex: number;
    revenue: number;
    orders: number;
    avgRevenue: number;
  }>;
}

export interface PromotionsSummary {
  promotions: {
    totalDiscount: number;
    usageCount: number;
    uniquePromotionsUsed: number;
  };
  discounts: {
    totalDiscount: number;
    ordersWithDiscount: number;
    totalOrders: number;
    discountRate: number;
  };
}

export interface CancelledOrders {
  cancelled: { count: number; value: number };
  voided: { count: number; value: number };
  total: { count: number; value: number };
  totalOrders: number;
  cancelRate: number;
}

export interface LoyaltySummary {
  totalMembers: number;
  newMembersToday: number;
  tierDistribution: Array<{
    tier: string;
    count: number;
  }>;
}

// ================================
// HOOKS
// ================================

/**
 * Fetch executive summary (KPIs with comparison)
 */
export function useExecutiveSummary(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'summary', branchId, dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<ExecutiveSummary>>(
        `/executive/summary?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 30 * 1000 : false, // Only auto-refresh for today
  });
}

/**
 * Fetch hourly sales
 */
export function useHourlySales(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'hourly-sales', branchId, dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<HourlySales>>(
        `/executive/hourly-sales?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 60 * 1000 : false,
  });
}

/**
 * Fetch top selling items
 */
export function useTopSellingItems(branchId?: string, limit = 5, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'top-items', branchId, limit, dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      params.append('limit', limit.toString());
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<{ items: TopSellingItem[] }>>(
        `/executive/top-items?${params.toString()}`
      );
      return response.data.data.items;
    },
    staleTime: 60 * 1000,
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 60 * 1000 : false,
  });
}

/**
 * Fetch branch performance (HQ only)
 */
export function useBranchPerformance(dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'branch-performance', dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<{ branches: BranchPerformance[] }>>(
        `/executive/branch-performance?${params.toString()}`
      );
      return response.data.data.branches;
    },
    staleTime: 60 * 1000,
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 60 * 1000 : false,
  });
}

/**
 * Fetch alerts (things needing attention)
 */
export function useExecutiveAlerts(branchId?: string) {
  return useQuery({
    queryKey: ['executive', 'alerts', branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<ExecutiveAlerts>>(
        `/executive/alerts?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/**
 * Fetch recent orders
 */
export function useRecentOrders(branchId?: string, limit = 10) {
  return useQuery({
    queryKey: ['executive', 'recent-orders', branchId, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      params.append('limit', limit.toString());

      const response = await apiClient.get<APIResponse<{ orders: RecentOrder[] }>>(
        `/executive/recent-orders?${params.toString()}`
      );
      return response.data.data.orders;
    },
    staleTime: 10 * 1000, // 10 seconds for live feed
    refetchInterval: 10 * 1000, // More frequent for recent orders
  });
}

/**
 * Fetch payment breakdown
 */
export function usePaymentBreakdown(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'payment-breakdown', branchId, dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<PaymentBreakdown>>(
        `/executive/payment-breakdown?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 60 * 1000,
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 60 * 1000 : false,
  });
}

/**
 * Fetch category sales
 */
export function useCategorySales(branchId?: string, limit = 5, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'category-sales', branchId, limit, dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      params.append('limit', limit.toString());
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<CategorySales>>(
        `/executive/category-sales?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 60 * 1000,
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 60 * 1000 : false,
  });
}

/**
 * Fetch sales by day of week (last 4 weeks)
 */
export function useSalesByDay(branchId?: string) {
  return useQuery({
    queryKey: ['executive', 'sales-by-day', branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<SalesByDay>>(
        `/executive/sales-by-day?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (less frequently updated)
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Fetch promotions summary
 */
export function usePromotionsSummary(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'promotions-summary', branchId, dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<PromotionsSummary>>(
        `/executive/promotions-summary?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 60 * 1000,
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 60 * 1000 : false,
  });
}

/**
 * Fetch cancelled/voided orders
 */
export function useCancelledOrders(branchId?: string, dateParams?: DateRangeParams) {
  return useQuery({
    queryKey: ['executive', 'cancelled-orders', branchId, dateParams],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId && branchId !== 'all') params.append('branchId', branchId);
      const dateStr = buildDateParams(dateParams);
      if (dateStr) {
        dateStr.split('&').forEach(p => {
          const [k, v] = p.split('=');
          if (k && v) params.append(k, v);
        });
      }

      const response = await apiClient.get<APIResponse<CancelledOrders>>(
        `/executive/cancelled-orders?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 60 * 1000,
    refetchInterval: dateParams?.preset === 'today' || !dateParams ? 60 * 1000 : false,
  });
}

/**
 * Fetch loyalty summary
 */
export function useLoyaltySummary() {
  return useQuery({
    queryKey: ['executive', 'loyalty-summary'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<LoyaltySummary>>(
        `/executive/loyalty-summary`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000,
  });
}
